import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { draftText, draftState, isDraftFailure, usableDraftText, variantLabel } from "@/lib/engagementDraft";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ContactsPanel } from "@/pages/Contacts";
import { CopyIcon } from "@/pages/ClientPortal";
import { VoiceChip, voiceColor } from "@/lib/voiceColors";
import {
  Plus,
  Copy,
  Check,
  X,
  RefreshCw,
  Pencil,
  Sparkles,
  Link2,
  ExternalLink,
  Radar,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Search,
  CheckSquare,
  Square,
  Users,
  Wand2,
  Reply,
  Trash2,
} from "lucide-react";

const FILTERS = [
  { k: "today", label: "Today" },
  { k: "yesterday", label: "Yesterday" },
  { k: "week", label: "This week" },
  { k: "missed", label: "Missed" },
  { k: "all", label: "All" },
];

export default function Feed() {
  const { workspaceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedMode = searchParams.get("mode");
  const mode = ["respond", "contacts", "inspire", "reply-lab"].includes(requestedMode) ? requestedMode : "engage";
  const [reviewDirty, setReviewDirty] = useState(false);
  const setMode = (next) => {
    if (reviewDirty) { toast.info("Save or cancel your edit before changing tools."); return; }
    setSearchParams(next === "engage" ? {} : { mode: next });
  };
  const [filter, setFilter] = useState("week");
  const [includeDone, setIncludeDone] = useState(false);
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [counts, setCounts] = useState({ today: 0, yesterday: 0, week: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [integrations, setIntegrations] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const loadSequence = useRef(0);

  useEffect(() => {
    apiGet("/settings/integrations").then(setIntegrations).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const request = ++loadSequence.current;
    setLoading(true);
    setLoadError(false);
    try {
      const [r, cs, ctsResp, vps] = await Promise.all([
        apiGet(`/workspaces/${workspaceId}/feed`, { filter, include_done: includeDone }),
        apiGet(`/workspaces/${workspaceId}/contacts`),
        apiGet(`/workspaces/${workspaceId}/feed/counts`).catch(() => ({})),
        apiGet(`/workspaces/${workspaceId}/voice_profiles`).catch(() => []),
      ]);
      if (request !== loadSequence.current) return;
      setItems(r.items || []);
      setContacts(cs || []);
      setCounts(ctsResp || {});
      setVoiceProfiles(vps || []);
    } catch (_) {
      if (request === loadSequence.current) setLoadError(true);
    } finally {
      if (request === loadSequence.current) setLoading(false);
    }
  }, [workspaceId, filter, includeDone]);

  useEffect(() => {
    setItems([]);
    load();
    return () => { loadSequence.current += 1; };
  }, [load]);

  const handleScrape = async () => {
    setScraping(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/scrape`, {});
      const runId = r.run_id;
      toast.success("scrape started — running in the background", {
        description: "1-3 min depending on contact count. you can keep working.",
      });
      // Poll the run status every 5s so we can refresh the feed automatically
      // and surface a final toast. No blocking request — survives any reload.
      const startedAt = Date.now();
      const maxMs = 6 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const s = await apiGet(
            `/workspaces/${workspaceId}/scrape/status${runId ? `?run_id=${runId}` : ""}`
          );
          if (s?.status === "succeeded") {
            const res = s.result || {};
            toast.success(`scrape done · ${res.new_posts || 0} new posts · ${res.drafts_created || 0} drafts`);
            load();
            break;
          }
          if (s?.status === "failed") {
            toast.error(s.error || "scrape failed");
            break;
          }
          // status === "running" → keep polling
        } catch {
          // transient — keep polling
          continue;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "scrape failed to start");
    } finally {
      setScraping(false);
    }
  };

  // Count drafts with no feedback yet — used to make feedback UI prominent for first 10
  const totalFeedback = items.reduce(
    (n, it) => n + ((it.draft?.feedback?.length || 0) > 0 ? 1 : 0),
    0,
  );
  const prominentFeedback = totalFeedback < 10;

  return (
    <Layout
      title="Engagement"
      subtitle="Review a post, refine the comment, then open LinkedIn to publish."
      actions={
        <>
          {mode === "engage" && integrations?.apify_configured && (
            <Button
              onClick={handleScrape}
              disabled={scraping}
              className="btn-ghost"
              data-testid="scrape-now-btn"
            >
              <Radar className={`w-3.5 h-3.5 mr-1.5 ${scraping ? "animate-pulse" : ""}`} />
              {scraping ? "Scanning…" : "Scrape now"}
            </Button>
          )}
          {mode === "engage" && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-post-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add post
                </Button>
              </DialogTrigger>
              <ManualPostDialog
                workspaceId={workspaceId}
                contacts={contacts}
                onClose={() => setDialogOpen(false)}
                onCreated={load}
              />
            </Dialog>
          )}
        </>
      }
    >
      {/* MODE TOGGLE */}
      <div className="flex flex-wrap items-center gap-1 mb-6 bg-white rounded-xl p-1 border border-slate-200 w-fit max-w-full" data-testid="feed-mode-toggle">
        <button
          onClick={() => setMode("engage")}
          data-testid="mode-engage"
          aria-pressed={mode === "engage"}
          className={`text-xs px-4 py-2 rounded-lg transition-colors font-semibold ${
            mode === "engage" ? "bg-graphite text-terracotta shadow-sm font-bold" : "text-stone hover:text-parchment"
          }`}
        >
          Prospect posts
        </button>
        <button
          onClick={() => setMode("respond")}
          data-testid="mode-respond"
          aria-pressed={mode === "respond"}
          className={`text-xs px-4 py-2 rounded-lg transition-colors font-semibold ${
            mode === "respond" ? "bg-graphite text-terracotta shadow-sm font-bold" : "text-stone hover:text-parchment"
          }`}
        >
          Replies to my posts
        </button>
        <button
          onClick={() => setMode("contacts")}
          data-testid="mode-contacts"
          aria-pressed={mode === "contacts"}
          className={`text-xs px-4 py-2 rounded-lg transition-colors font-semibold inline-flex items-center gap-1.5 ${
            mode === "contacts" ? "bg-graphite text-terracotta shadow-sm font-bold" : "text-stone hover:text-parchment"
          }`}
        >
          <Users className="w-3 h-3" />
          Contacts
        </button>
        <button
          onClick={() => setMode("inspire")}
          data-testid="mode-inspire"
          aria-pressed={mode === "inspire"}
          className={`text-xs px-4 py-2 rounded-lg transition-colors font-semibold inline-flex items-center gap-1.5 ${
            mode === "inspire" ? "bg-graphite text-terracotta shadow-sm font-bold" : "text-stone hover:text-parchment"
          }`}
        >
          <Wand2 className="w-3 h-3" />
          Comment generator
        </button>
        <button
          onClick={() => setMode("reply-lab")}
          data-testid="mode-reply-lab"
          aria-pressed={mode === "reply-lab"}
          className={`text-xs px-4 py-2 rounded-lg transition-colors font-semibold inline-flex items-center gap-1.5 ${
            mode === "reply-lab" ? "bg-graphite text-terracotta shadow-sm font-bold" : "text-stone hover:text-parchment"
          }`}
        >
          <Reply className="w-3 h-3" />
          Reply generator
        </button>
      </div>

      {mode === "respond" ? (
        <RespondPanel workspaceId={workspaceId} integrations={integrations} />
      ) : mode === "contacts" ? (
        <ContactsPanel workspaceId={workspaceId} />
      ) : mode === "inspire" ? (
        <InspirePanel workspaceId={workspaceId} />
      ) : mode === "reply-lab" ? (
        <ReplyLabPanel workspaceId={workspaceId} voiceProfiles={voiceProfiles} />
      ) : (
        <EngagePanel
          key={workspaceId}
          loadError={loadError}
          onDirtyChange={setReviewDirty}
          filter={filter}
          setFilter={setFilter}
          includeDone={includeDone}
          setIncludeDone={setIncludeDone}
          items={items}
          counts={counts}
          loading={loading}
          contacts={contacts}
          voiceProfiles={voiceProfiles}
          workspaceId={workspaceId}
          prominentFeedback={prominentFeedback}
          onLoad={load}
          onOpenAdd={() => setDialogOpen(true)}
        />
      )}
    </Layout>
  );
}

function EngagePanel({
  filter, setFilter, includeDone, setIncludeDone, items, counts, loading,
  loadError, contacts, voiceProfiles, workspaceId, prominentFeedback, onLoad, onOpenAdd, onDirtyChange,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  const matching = items.filter((item) => `${item.contact?.name || ""} ${item.post.post_text || ""}`.toLowerCase().includes(query.toLowerCase()));
  const selected = matching.find((item) => item.post.id === selectedId) || matching[0];
  const changeView = (change) => {
    if (dirty) {
      toast.info("Save or cancel your edit before changing the queue.");
      return;
    }
    change();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <Tabs value={filter} onValueChange={(value) => changeView(() => setFilter(value))} className="min-w-0 max-w-full">
          <TabsList data-testid="feed-filter-tabs" className="flex flex-wrap h-auto justify-start bg-white border border-slate-200 rounded-xl p-1">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.k} value={f.k} data-testid={`filter-${f.k}`}
                className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 text-slate-600 rounded-lg px-3 py-2 text-xs gap-1.5">
                {f.label}
                {counts[f.k] > 0 && <span className="text-[10px] tabular-nums">{counts[f.k].toLocaleString()}</span>}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <Switch data-testid="include-done" checked={includeDone} onCheckedChange={(value) => changeView(() => setIncludeDone(value))} />
          Show completed
        </label>
      </div>

      {loadError ? (
        <div role="alert" className="ed-card mb-4">
          <p className="text-sm mb-3">The engagement queue couldn’t be loaded. Please try again.</p>
          <Button className="btn-primary" onClick={onLoad}>Try again</Button>
        </div>
      ) : null}
      {loading && items.length === 0 ? (
        <div role="status" className="text-sm text-slate-500 py-8">Loading your queue…</div>
      ) : items.length === 0 && !loadError ? (
        <EmptyFeed contacts={contacts} onAdd={onOpenAdd} filter={filter} includeDone={includeDone} />
      ) : items.length > 0 ? (
        <div className="engagement-review-grid" aria-busy={loading}>
          <aside className="review-queue" aria-label="Posts to review">
            <div className="p-4 border-b border-slate-200">
              <div className="flex flex-wrap gap-2 justify-between items-center">
                <h2 className="text-sm font-semibold text-slate-900">Review queue · {matching.length}</h2>
                <button className="xl:hidden text-xs font-semibold text-indigo-700" aria-expanded={queueOpen}
                  onClick={() => setQueueOpen((open) => !open)} data-testid="toggle-review-queue">
                  {queueOpen ? "Close list" : "Choose post"}
                </button>
              </div>
              <div className={queueOpen ? "mt-3" : "hidden xl:block mt-3"}>
              <Input aria-label="Search posts or people" placeholder="Search posts or people" value={query}
                onChange={(e) => changeView(() => setQuery(e.target.value))} />
              </div>
            </div>
            <div className={`review-queue-items ${queueOpen ? "" : "hidden xl:block"}`}>
              {matching.map((item) => {
                const state = draftState(item.draft);
                return (
                  <button key={item.post.id} data-testid={`queue-post-${item.post.id}`}
                    aria-pressed={selected?.post.id === item.post.id}
                    onClick={() => changeView(() => { setSelectedId(item.post.id); setQueueOpen(false); })}
                    className={`review-queue-item ${selected?.post.id === item.post.id ? "is-selected" : ""}`}>
                    <span className="block text-sm font-semibold truncate">{item.contact?.name || "Unknown author"}</span>
                    <span className="block text-xs text-slate-500 leading-relaxed mt-1 line-clamp-2">{item.post.post_text || "Post without text"}</span>
                    <span className={`inline-block text-[11px] mt-2 ${state === "Needs repair" ? "text-amber-700" : "text-slate-500"}`}>{state}</span>
                  </button>
                );
              })}
              {matching.length === 0 && <p className="p-4 text-sm text-slate-500">No matching posts. Try another search.</p>}
            </div>
          </aside>
          <section className="min-w-0" aria-label="Selected post">
            {selected ? (
              <PostCard key={selected.post.id} item={selected} workspaceId={workspaceId}
                voiceProfiles={voiceProfiles} prominentFeedback={prominentFeedback}
                onChange={onLoad} onDirtyChange={setDirty} />
            ) : <div className="ed-card text-sm text-slate-500">Choose a post from the queue.</div>}
          </section>
        </div>
      ) : null}
    </>
  );
}

function EmptyFeed({ contacts, onAdd, filter, includeDone }) {
  return (
    <div className="surface p-12 text-center max-w-xl mx-auto fade-up">
      <h2 className="heading text-2xl text-zinc-100 mb-2">No posts here yet.</h2>
      <p className="text-sm text-zinc-400 mb-6 max-w-md mx-auto">
        {contacts.length === 0
          ? "Add target contacts first (bulk paste LinkedIn URLs is fastest), then trigger a scrape."
          : filter === "today"
          ? "No posts dated today on LinkedIn. Try 'This week' or 'Show completed'."
          : "Try switching filters or scraping again."}
      </p>
      <Button onClick={onAdd} className="btn-primary" data-testid="empty-add-post">
        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add a post manually
      </Button>
    </div>
  );
}

function PostCard({ item, workspaceId, voiceProfiles, prominentFeedback, onChange, onDirtyChange, delay = 0 }) {
  const { post, draft, contact } = item;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [regen, setRegen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFbNote, setShowFbNote] = useState(false);
  const [fbNote, setFbNote] = useState("");

  // Variants: if draft has them, group by voice_profile_name. Else legacy single.
  const variants = React.useMemo(() => draft?.variants || [], [draft?.variants]);
  const hasVariants = variants.length > 0;
  const selectedId = draft?.selected_variant_id || (variants[0]?.variant_id ?? null);
  const selectedVariant = variants.find((v) => v.variant_id === selectedId) || variants[0] || null;

  // Group variants by voice
  const voiceGroups = React.useMemo(() => {
    if (!hasVariants) return [];
    const map = new Map();
    for (const v of variants) {
      const key = v.voice_profile_id || "_default";
      if (!map.has(key)) {
        map.set(key, { id: key, name: v.voice_profile_name || "Primary", variants: [] });
      }
      map.get(key).variants.push(v);
    }
    return Array.from(map.values());
  }, [variants, hasVariants]);

  const initialVoiceId = selectedVariant?.voice_profile_id || voiceGroups[0]?.id || "_default";
  const [activeVoiceId, setActiveVoiceId] = useState(initialVoiceId);
  React.useEffect(() => {
    setActiveVoiceId(selectedVariant?.voice_profile_id || voiceGroups[0]?.id || "_default");
  }, [selectedVariant?.variant_id, selectedVariant?.voice_profile_id, voiceGroups]);

  const visibleVariants = hasVariants
    ? voiceGroups.find((g) => g.id === activeVoiceId)?.variants || []
    : [];

  const rawText = draftText(draft);
  const baseText = usableDraftText(rawText);
  const failed = isDraftFailure(rawText);
  const [text, setText] = useState(baseText);
  const dirty = editing && text !== baseText;
  useEffect(() => {
    setText(baseText);
    setEditing(false);
  }, [baseText, draft?.selected_variant_id]);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const canUse = !!baseText && !failed;

  const isIcp = !!(contact?.icp_match || draft?.icp_match);
  const isUsed = draft?.status === "used";
  const isSkipped = draft?.status === "skipped";
  const myFeedback = draft?.feedback?.length || 0;
  const lastFeedback = draft?.feedback?.[draft.feedback.length - 1];

  const pickVariant = async (vId) => {
    if (!draft || saving || regen || editing) return;
    setSaving(true);
    try {
      await apiPost(`/workspaces/${workspaceId}/drafts/${draft.id}/select-variant`, {
        variant_id: vId,
      });
      await onChange();
    } catch (e) {
      toast.error("Failed to switch variant");
    } finally { setSaving(false); }
  };

  const copyComment = async () => {
    const val = usableDraftText(editing ? text : rawText);
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success(post.post_url ? "Copied — opening LinkedIn…" : "Comment copied");
      if (draft && draft.status === "pending") {
        try {
          await apiPatch(`/workspaces/${workspaceId}/drafts/${draft.id}`, { status: "reviewed" });
        } catch (_) {}
      }
      if (post.post_url) {
        // Must be triggered from same user gesture for browsers to allow popup
        window.open(post.post_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const setStatus = async (status) => {
    if (!draft || saving || (status === "used" && !canUse)) return;
    setSaving(true);
    try {
      await apiPatch(`/workspaces/${workspaceId}/drafts/${draft.id}`, { status });
      toast.success(status === "used" ? "Marked done" : "Post skipped");
      await onChange();
    } catch (_) {
      toast.error("Could not update this post. Please try again.");
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!draft || saving || !usableDraftText(text)) return;
    setSaving(true);
    try {
      await apiPatch(`/workspaces/${workspaceId}/drafts/${draft.id}`, { edited_comment: text.trim() });
      setEditing(false);
      toast.success("Edit saved");
      await onChange();
    } catch (_) {
      toast.error("Your edit wasn’t saved. It is still here; please try again.");
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    if (!draft || editing || saving) return;
    setRegen(true);
    try {
      await apiPost(`/workspaces/${workspaceId}/drafts/${draft.id}/regenerate`, {});
      await onChange();
    } catch (e) {
      toast.error("Regenerate failed");
    } finally {
      setRegen(false);
    }
  };

  const sendFeedback = async (rating) => {
    if (!draft) return;
    try {
      await apiPost(`/workspaces/${workspaceId}/drafts/${draft.id}/feedback`, {
        rating,
        note: fbNote.trim(),
      });
      setFbNote("");
      setShowFbNote(false);
      toast.success(rating === "up" ? "Thanks, voice will sharpen" : "Got it, noted");
      onChange();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const snippet = post.post_text || "";
  const shouldTruncate = snippet.length > 240 && !expanded;
  const visibleText = shouldTruncate ? snippet.slice(0, 240).trim() + "…" : snippet;
  const initials = (contact?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <article
      data-testid={`post-card-${post.id}`}
      className={`surface review-post ${isIcp ? "icp-pulse" : ""} ${isUsed || isSkipped ? "opacity-70" : ""} overflow-hidden fade-up`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <header className="px-5 pt-4 pb-3 flex items-start justify-between gap-3 border-b border-zinc-800/60">
        <div className="flex items-center gap-3 min-w-0">
          {contact?.avatar_url ? (
            <img src={contact.avatar_url} alt={contact.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-sm text-zinc-300 shrink-0">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm text-zinc-100 truncate">{contact?.name || "Unknown"}</div>
            <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
              <span>{contact?.relationship || "—"}</span>
              {contact?.headline && <span className="truncate max-w-[280px]">· {contact.headline}</span>}
              {isIcp && (
                <span data-testid="icp-flag" className="text-sky-400 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> ICP
                </span>
              )}
              {post.post_url && (
                <a
                  href={post.post_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
                  data-testid={`open-post-${post.id}`}
                >
                  <ExternalLink className="w-3 h-3" /> open
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="overline text-right" style={{ fontSize: 9 }}>
          {isUsed ? "done" : isSkipped ? "skipped" : "pending"}
        </div>
      </header>

      <div className="px-5 py-4">
        <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap break-words">{visibleText}</p>
        {snippet.length > 240 && (
          <button
            onClick={() => setExpanded((x) => !x)}
            className="text-xs text-zinc-500 hover:text-zinc-300 mt-2"
            data-testid={`expand-post-${post.id}`}
          >
            {expanded ? "Collapse" : "Expand full post"}
          </button>
        )}
      </div>

      {draft && (
        <div className="px-5 pb-4">
          {/* Voice profile tabs (if multiple) */}
          {hasVariants && voiceGroups.length > 1 && (
            <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid={`voice-tabs-${draft.id}`}>
              {voiceGroups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => pickVariant(g.variants[0]?.variant_id)}
                  disabled={editing || saving || regen}
                  aria-pressed={activeVoiceId === g.id}
                  data-testid={`voice-tab-${draft.id}-${g.id}`}
                  className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                    activeVoiceId === g.id
                      ? "border-sky-400/50 text-sky-300 bg-sky-400/10"
                      : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          )}

          {/* Variant style chips */}
          {hasVariants && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid={`variant-chips-${draft.id}`}>
              {visibleVariants.map((v) => {
                const active = v.variant_id === selectedId;
                return (
                  <button
                    key={v.variant_id}
                    onClick={() => pickVariant(v.variant_id)}
                    disabled={editing || saving || regen}
                    aria-pressed={active}
                    data-testid={`variant-chip-${v.variant_id}`}
                    title={variantLabel(v.style)}
                    className={`text-xs mono px-2 py-1 rounded-sm border transition-colors ${
                      active
                        ? "border-zinc-300 text-zinc-100 bg-zinc-800"
                        : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                    }`}
                  >
                    {variantLabel(v.style)}
                  </button>
                );
              })}
            </div>
          )}

          <div className="overline mb-2 flex items-center gap-2 flex-wrap">
            <Sparkles className="w-3 h-3 text-sky-400" />
            ai draft
            {selectedVariant?.style && hasVariants && (
              <span className="text-zinc-400 normal-case tracking-normal lowercase" style={{ fontSize: 10 }}>
                · {variantLabel(selectedVariant.style)} · {selectedVariant.voice_profile_name}
              </span>
            )}
            {!hasVariants && draft.reasoning && !isDraftFailure(draft.reasoning) && (
              <span className="text-zinc-500 normal-case tracking-normal lowercase" style={{ fontSize: 10 }}>
                · {draft.reasoning}
              </span>
            )}
          </div>

          {editing ? (
            <Textarea
              aria-label="Edit comment"
              data-testid={`edit-comment-${draft.id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 mb-3"
            />
          ) : (
            <div
              className="bg-zinc-950/60 border border-zinc-800 rounded-md px-4 py-3 text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap break-words"
              data-testid={`draft-text-${draft.id}`}
            >
              {failed ? <span role="status" className="text-amber-700">This draft could not be generated. Try regenerating it, or write a comment using Edit. Failed output cannot be copied or marked done.</span> : baseText || <span className="text-zinc-500">No draft generated. Write a comment using Edit or regenerate.</span>}
            </div>
          )}

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {editing ? (
              <>
                <Button onClick={saveEdit} disabled={saving || !usableDraftText(text)} className="btn-primary" data-testid={`save-edit-${draft.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Save edit
                </Button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setText(baseText);
                  }}
                  disabled={saving}
                  className="btn-ghost"
                  data-testid={`cancel-edit-${draft.id}`}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <Button onClick={copyComment} disabled={!canUse || saving || regen} className="btn-primary" data-testid={`copy-${draft.id}`}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> {post.post_url ? "Copy + open post" : "Copy comment"}
                </Button>
                <button
                  onClick={() => setEditing(true)}
                  disabled={saving || regen}
                  className="btn-ghost inline-flex items-center"
                  data-testid={`edit-${draft.id}`}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </button>
                <button
                  onClick={regenerate}
                  disabled={regen || saving}
                  className="btn-ghost inline-flex items-center"
                  data-testid={`regen-${draft.id}`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regen ? "animate-spin" : ""}`} />
                  {regen ? "Regenerating all…" : "Regenerate all"}
                </button>
                <div className="flex-1" />
                <button
                  onClick={() => setStatus("skipped")}
                  disabled={saving || regen}
                  className="btn-ghost inline-flex items-center"
                  data-testid={`skip-${draft.id}`}
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Skip
                </button>
                <Button onClick={() => setStatus("used")} disabled={!canUse || saving || regen} className="btn-primary" data-testid={`done-${draft.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Mark done
                </Button>
              </>
            )}
          </div>

          {/* Feedback row */}
          {!editing && canUse && (
            <div
              className={`mt-3 pt-3 border-t border-zinc-800/60 ${prominentFeedback ? "" : "opacity-60"}`}
              data-testid={`feedback-row-${draft.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-zinc-500">
                  {myFeedback === 0 ? (
                    prominentFeedback ? (
                      <span className="text-zinc-300">rate this draft, helps the voice learn</span>
                    ) : (
                      "feedback (optional)"
                    )
                  ) : (
                    <span>
                      {lastFeedback?.rating === "up" ? "rated up" : "rated down"}
                      {lastFeedback?.note ? ` · "${lastFeedback.note.slice(0, 60)}"` : ""}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => sendFeedback("up")}
                    data-testid={`thumb-up-${draft.id}`}
                    className={`p-1.5 rounded-md border transition-colors ${
                      lastFeedback?.rating === "up"
                        ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10"
                        : "border-zinc-800 text-zinc-500 hover:text-emerald-300 hover:border-zinc-700"
                    }`}
                    aria-label="Thumb up"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => sendFeedback("down")}
                    data-testid={`thumb-down-${draft.id}`}
                    className={`p-1.5 rounded-md border transition-colors ${
                      lastFeedback?.rating === "down"
                        ? "border-rose-500/50 text-rose-400 bg-rose-500/10"
                        : "border-zinc-800 text-zinc-500 hover:text-rose-300 hover:border-zinc-700"
                    }`}
                    aria-label="Thumb down"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setShowFbNote((x) => !x)}
                    data-testid={`fb-note-toggle-${draft.id}`}
                    className="p-1.5 rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-200"
                    aria-label="Add note"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {showFbNote && (
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid={`fb-note-${draft.id}`}
                    value={fbNote}
                    onChange={(e) => setFbNote(e.target.value)}
                    placeholder="why? (e.g. 'too generic', 'tone too formal', 'great hook')"
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 text-sm h-8"
                  />
                  <button
                    onClick={() => sendFeedback("up")}
                    className="btn-ghost text-xs"
                    data-testid={`fb-save-up-${draft.id}`}
                  >
                    save 👍
                  </button>
                  <button
                    onClick={() => sendFeedback("down")}
                    className="btn-ghost text-xs"
                    data-testid={`fb-save-down-${draft.id}`}
                  >
                    save 👎
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function ManualPostDialog({ workspaceId, contacts, onClose, onCreated }) {
  const [contactId, setContactId] = useState("");
  const [postText, setPostText] = useState("");
  const [postUrl, setPostUrl] = useState("");
  const [creating, setCreating] = useState(false);

  const submit = async () => {
    if (!contactId) {
      toast.error("Choose a contact");
      return;
    }
    if (!postText.trim()) {
      toast.error("Paste the post text");
      return;
    }
    setCreating(true);
    try {
      await apiPost(`/workspaces/${workspaceId}/posts/manual`, {
        contact_id: contactId,
        post_text: postText.trim(),
        post_url: postUrl.trim(),
        media_type: "text",
      });
      toast.success("Post added — AI generated a draft");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add post");
    } finally {
      setCreating(false);
    }
  };

  return (
    <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="manual-post-dialog">
      <DialogHeader>
        <DialogTitle className="heading text-xl">Add a post manually</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">Contact</Label>
          {contacts.length === 0 ? (
            <div className="text-sm text-zinc-400 surface p-3">
              Add contacts first — go to Contacts tab.
            </div>
          ) : (
            <select
              data-testid="manual-contact-select"
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select a contact…</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.icp_match ? "★" : ""}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">Post URL (optional)</Label>
          <div className="relative">
            <Link2 className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="manual-post-url"
              value={postUrl}
              onChange={(e) => setPostUrl(e.target.value)}
              placeholder="https://linkedin.com/posts/…"
              className="bg-zinc-950 border-zinc-800 text-zinc-100 pl-9"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-300 mb-1.5 block">Post text *</Label>
          <Textarea
            data-testid="manual-post-text"
            value={postText}
            onChange={(e) => setPostText(e.target.value)}
            rows={7}
            placeholder="Paste the full LinkedIn post text here…"
            className="bg-zinc-950 border-zinc-800 text-zinc-100"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost" data-testid="manual-cancel">
            Cancel
          </button>
          <Button
            onClick={submit}
            disabled={creating || contacts.length === 0}
            className="btn-primary"
            data-testid="manual-submit"
          >
            {creating ? "Generating draft…" : "Add post + draft"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}


// ============================================================
// RESPOND PANEL — replies to comments on YOUR own posts
// ============================================================
function RespondPanel({ workspaceId, integrations }) {
  const [filter, setFilter] = useState("week");
  const [includeDone, setIncludeDone] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ today: 0, yesterday: 0, week: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  // Bulk selection (set of reply.id values to support cross-card selection)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Fading-out ids (visual cue before they leave the DOM)
  const [fadingIds, setFadingIds] = useState(() => new Set());
  // Bulk mutation in flight
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        apiGet(`/workspaces/${workspaceId}/my-posts/feed`, { filter, include_done: includeDone }),
        apiGet(`/workspaces/${workspaceId}/my-posts/counts`).catch(() => ({})),
      ]);
      setItems(r.items || []);
      setCounts(c || {});
      setSelectedIds(new Set());
      setFadingIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, filter, includeDone]);

  // All pending reply ids across all cards (for select-all)
  const allPendingIds = React.useMemo(() => {
    const ids = [];
    items.forEach((it) => {
      (it.threads || []).forEach((t) => {
        if (t.reply && (t.reply.status === "pending" || !t.reply.status)) ids.push(t.reply.id);
      });
    });
    return ids;
  }, [items]);

  const allSelected = allPendingIds.length > 0 && allPendingIds.every((id) => selectedIds.has(id));

  const toggleSelect = useCallback((replyId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(replyId)) next.delete(replyId);
      else next.add(replyId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (allPendingIds.length === 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const everySelected = allPendingIds.every((id) => next.has(id));
      if (everySelected) {
        allPendingIds.forEach((id) => next.delete(id));
      } else {
        allPendingIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPendingIds]);

  const clearSelection = () => setSelectedIds(new Set());

  // Optimistic single-item update (used by per-thread Mark done / Skip)
  const optimisticPatchReply = useCallback((replyId, newStatus) => {
    // Fade-out animation, then remove (unless includeDone is on)
    setFadingIds((prev) => new Set(prev).add(replyId));
    setTimeout(() => {
      setItems((prev) => {
        return prev
          .map((it) => {
            const threads = (it.threads || [])
              .map((t) => {
                if (t.reply && t.reply.id === replyId) {
                  if (!includeDone && (newStatus === "used" || newStatus === "skipped")) {
                    return null; // hide
                  }
                  return { ...t, reply: { ...t.reply, status: newStatus } };
                }
                return t;
              })
              .filter(Boolean);
            const pending_count = threads.filter(
              (t) => t.reply && t.reply.status === "pending"
            ).length;
            return { ...it, threads, pending_count };
          })
          // optionally drop posts with zero threads when not showing done
          .filter((it) => includeDone || (it.threads && it.threads.length > 0));
      });
      setFadingIds((prev) => {
        const next = new Set(prev);
        next.delete(replyId);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(replyId);
        return next;
      });
    }, 280); // matches CSS fade-out duration
  }, [includeDone]);

  const markUsed = async (replyId) => {
    optimisticPatchReply(replyId, "used");
    try {
      await apiPatch(`/workspaces/${workspaceId}/reply-drafts/${replyId}`, { status: "used" });
    } catch (e) {
      toast.error("mark done failed — refreshing");
      load();
    }
  };

  const skipReply = async (replyId) => {
    optimisticPatchReply(replyId, "skipped");
    try {
      await apiPatch(`/workspaces/${workspaceId}/reply-drafts/${replyId}`, { status: "skipped" });
    } catch (e) {
      toast.error("skip failed — refreshing");
      load();
    }
  };

  const bulkApply = async (newStatus) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    // Optimistic — fade them all out
    ids.forEach((id) => optimisticPatchReply(id, newStatus));
    try {
      await Promise.all(
        ids.map((id) =>
          apiPatch(`/workspaces/${workspaceId}/reply-drafts/${id}`, { status: newStatus }).catch(() => null)
        )
      );
      // Undoable toast
      toast.success(
        `${ids.length} ${newStatus === "used" ? "marked done" : "skipped"}`,
        {
          duration: 8000,
          action: {
            label: "undo",
            onClick: async () => {
              try {
                await apiPost("/operator/bulk-undo", { kind: "reply_drafts", ids });
                toast.success(`${ids.length} restored`);
                load();
              } catch (e) {
                toast.error("undo failed");
              }
            },
          },
        }
      );
    } finally {
      setBulkBusy(false);
    }
  };

  // Keyboard shortcuts: E to mark done (or skip), A to select all, Escape to clear
  useEffect(() => {
    const onKey = (e) => {
      // Don't trigger when user is typing
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey) {
        if (e.key === "a" && allPendingIds.length > 0) {
          e.preventDefault();
          selectAll();
        }
        return;
      }
      if (e.key === "Escape") {
        clearSelection();
      } else if ((e.key === "e" || e.key === "E") && selectedIds.size > 0) {
        e.preventDefault();
        bulkApply("used");
      } else if ((e.key === "s" || e.key === "S") && selectedIds.size > 0) {
        e.preventDefault();
        bulkApply("skipped");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, allPendingIds]);

  const handleScrape = async () => {
    setScraping(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/my-posts/scrape`, {});
      const runId = r.run_id;
      toast.success("scan started — running in the background", {
        description: "usually 1-3 min. you can keep working.",
      });
      const startedAt = Date.now();
      const maxMs = 6 * 60 * 1000;
      let finalised = false;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const s = await apiGet(
            `/workspaces/${workspaceId}/my-posts/scrape/status${runId ? `?run_id=${runId}` : ""}`
          );
          if (s?.status === "succeeded") {
            const res = s.result || {};
            const newPosts = res.new_posts ?? res.posts_added ?? 0;
            const newComments = res.new_comments ?? res.comments_added ?? 0;
            const newReplies = res.reply_drafts_created ?? res.replies_created ?? 0;
            if (newPosts === 0 && newComments === 0) {
              toast.message("scan complete · no new posts or comments found", {
                description: "if you expected results, check Settings → Voice profiles → LinkedIn URL (must be the profile whose posts you want to scan).",
                duration: 9000,
              });
            } else {
              toast.success(`${newPosts} new posts · ${newComments} new comments · ${newReplies} drafts`);
            }
            await load();
            finalised = true;
            break;
          }
          if (s?.status === "failed") {
            toast.error(s.error || "scan failed", { duration: 10000 });
            finalised = true;
            break;
          }
          // running → keep polling
        } catch {
          // transient — keep polling
          continue;
        }
      }
      if (!finalised) {
        toast.warning("scan is taking longer than usual — check back in a few minutes");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "scan failed to start");
    } finally {
      setScraping(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList
            data-testid="respond-filter-tabs"
            className="bg-graphite hairline p-1"
          >
            {FILTERS.map((f) => {
              const badge = counts[f.k];
              return (
                <TabsTrigger
                  key={f.k}
                  value={f.k}
                  data-testid={`respond-filter-${f.k}`}
                  className="data-[state=active]:bg-graphite-2 data-[state=active]:text-parchment text-stone px-3 py-1.5 text-sm inline-flex items-center gap-2 mono uppercase tracking-wider"
                  style={{ fontSize: 11 }}
                >
                  {f.label}
                  {badge !== undefined && badge > 0 && (
                    <span className="overline" style={{ fontSize: 9, color: "hsl(var(--accent))" }}>
                      {badge}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-stone cursor-pointer mr-2">
            <Switch
              data-testid="respond-include-done"
              checked={includeDone}
              onCheckedChange={setIncludeDone}
            />
            Show completed
          </label>
          {allPendingIds.length > 0 && (
            <button
              onClick={selectAll}
              data-testid="respond-select-all"
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
              title="select all (⌘A)"
            >
              {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {allSelected ? "deselect all" : `select all (${allPendingIds.length})`}
            </button>
          )}
          <Dialog open={findOpen} onOpenChange={setFindOpen}>
            <DialogTrigger asChild>
              <Button className="btn-ghost" data-testid="find-post-btn">
                <Search className="w-3.5 h-3.5 mr-1.5" /> Find post
              </Button>
            </DialogTrigger>
            <FindPostDialog
              workspaceId={workspaceId}
              onClose={() => setFindOpen(false)}
              onFound={() => { setFindOpen(false); load(); }}
            />
          </Dialog>
          {integrations?.apify_configured && (
            <Button
              onClick={handleScrape}
              disabled={scraping}
              className="btn-primary"
              data-testid="respond-scrape-btn"
            >
              <Radar className={`w-3.5 h-3.5 mr-1.5 ${scraping ? "animate-pulse" : ""}`} />
              {scraping ? "Scanning…" : "Scan my posts"}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="overline">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hairline px-12 py-16 text-center max-w-xl mx-auto fade-up" data-testid="respond-empty">
          <div className="overline mb-3">empty · filter: {filter}</div>
          <h2 className="serif text-2xl text-parchment mb-2">No replies waiting.</h2>
          <p className="text-sm text-stone mb-6 max-w-md mx-auto">
            Hit "Scan my posts" to pull recent posts from your voice profile LinkedIn URLs and auto-generate replies for every comment.
            Or paste a specific post URL using "Find post".
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-w-4xl pb-24">
          {items.map((it, idx) => (
            <MyPostCard
              key={it.post.id}
              item={it}
              workspaceId={workspaceId}
              onChange={load}
              delay={idx * 40}
              selectedIds={selectedIds}
              fadingIds={fadingIds}
              onToggleSelect={toggleSelect}
              onMarkUsed={markUsed}
              onSkip={skipReply}
            />
          ))}
        </div>
      )}

      {/* Floating bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          busy={bulkBusy}
          onMarkDone={() => bulkApply("used")}
          onSkip={() => bulkApply("skipped")}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}

function BulkActionBar({ count, busy, onMarkDone, onSkip, onClear }) {
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 fade-up"
      data-testid="respond-bulk-bar"
    >
      <div
        className="hairline px-6 py-3 flex items-center gap-4 shadow-2xl"
        style={{ background: "hsl(var(--graphite))", borderColor: "hsl(var(--accent) / 0.35)" }}
      >
        <span className="mono text-[11px] uppercase tracking-wider text-parchment">
          {count} selected
        </span>
        <div className="w-px h-5 bg-hairline" />
        <button
          onClick={onMarkDone}
          disabled={busy}
          data-testid="respond-bulk-mark-done"
          className="btn-primary text-xs inline-flex items-center gap-1.5"
          title="mark done (E)"
        >
          <Check className="w-3.5 h-3.5" />
          mark done <span className="mono text-[10px] opacity-60">E</span>
        </button>
        <button
          onClick={onSkip}
          disabled={busy}
          data-testid="respond-bulk-skip"
          className="btn-ghost text-xs inline-flex items-center gap-1.5"
          title="skip (S)"
        >
          <X className="w-3.5 h-3.5" />
          skip <span className="mono text-[10px] opacity-60">S</span>
        </button>
        <button
          onClick={onClear}
          data-testid="respond-bulk-clear"
          className="btn-text text-xs"
          title="clear (Esc)"
        >
          clear <span className="mono text-[10px] opacity-60">esc</span>
        </button>
      </div>
    </div>
  );
}

function MyPostCard({ item, workspaceId, onChange, delay = 0, selectedIds, fadingIds, onToggleSelect, onMarkUsed, onSkip }) {
  const { post, threads, pending_count, used_count = 0, total_comments = 0 } = item;
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const snippet = post.post_text || "";
  const shouldTruncate = snippet.length > 240 && !expanded;
  const visibleText = shouldTruncate ? snippet.slice(0, 240).trim() + "…" : snippet;

  const formatRelative = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const min = Math.floor(diff / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  };

  const refreshComments = async () => {
    setRefreshing(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/my-posts/${post.id}/refresh`, {});
      const scraped = r.scraped_comments ?? 0;
      const newOnes = r.comments_added ?? 0;
      if (newOnes > 0) {
        toast.success(`Refreshed · ${newOnes} new of ${scraped} total comments on LinkedIn`);
      } else {
        toast(`Refreshed · ${scraped} comments on LinkedIn, none new since last check`);
      }
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <article
      data-testid={`my-post-card-${post.id}`}
      className="surface overflow-hidden fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <header className="px-5 pt-4 pb-3 flex items-start justify-between gap-3 border-b border-zinc-800/60">
        <div className="min-w-0">
          <div className="text-sm text-zinc-100">Your post · <span className="text-sky-400">{post.voice_profile_name}</span></div>
          <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
            <span>{(post.post_date || "").slice(0, 10)}</span>
            {post.post_url && (
              <a
                href={post.post_url}
                target="_blank"
                rel="noreferrer"
                className="text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
                data-testid={`open-my-post-${post.id}`}
              >
                <ExternalLink className="w-3 h-3" /> open
              </a>
            )}
            <span>· {total_comments} comment{total_comments === 1 ? "" : "s"}</span>
            {pending_count > 0 && (
              <span className="text-sky-400">· {pending_count} pending</span>
            )}
            {used_count > 0 && (
              <span className="text-emerald-400">· {used_count} replied</span>
            )}
            {post.last_refreshed_at && (
              <span className="text-zinc-600" title={post.last_refreshed_at}>
                · refreshed {formatRelative(post.last_refreshed_at)}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Dialog open={addingComment} onOpenChange={setAddingComment}>
            <DialogTrigger asChild>
              <button
                data-testid={`add-comment-${post.id}`}
                className="btn-ghost text-xs"
                title="Add a comment manually (one at a time)"
              >
                <Plus className="w-3.5 h-3.5 mr-1 inline" />
                Add comment
              </button>
            </DialogTrigger>
            <ManualCommentDialog
              workspaceId={workspaceId}
              postId={post.id}
              onClose={() => setAddingComment(false)}
              onAdded={() => { setAddingComment(false); onChange(); }}
            />
          </Dialog>
          <Dialog open={bulkAdding} onOpenChange={setBulkAdding}>
            <DialogTrigger asChild>
              <button
                data-testid={`bulk-add-${post.id}`}
                className="btn-ghost text-xs"
                title="Paste multiple comments at once"
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1 inline" />
                Bulk add
              </button>
            </DialogTrigger>
            <BulkCommentDialog
              workspaceId={workspaceId}
              postId={post.id}
              onClose={() => setBulkAdding(false)}
              onAdded={() => { setBulkAdding(false); onChange(); }}
            />
          </Dialog>
          <button
            onClick={refreshComments}
            disabled={refreshing}
            data-testid={`refresh-post-${post.id}`}
            className="btn-ghost text-xs"
            title="Pull new comments via Apify"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 inline ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="px-5 py-4 border-b border-zinc-800/60">
        <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{visibleText}</p>
        {snippet.length > 240 && (
          <button
            onClick={() => setExpanded((x) => !x)}
            className="text-xs text-zinc-500 hover:text-zinc-300 mt-2"
            data-testid={`expand-my-post-${post.id}`}
          >
            {expanded ? "Collapse" : "Expand full post"}
          </button>
        )}
      </div>

      {threads.length > 0 ? (
        <div className="px-5 py-4 space-y-4">
          {threads.map((t) => (
            <CommentThread
              key={t.comment.id}
              thread={t}
              workspaceId={workspaceId}
              onChange={onChange}
              isSelected={t.reply && selectedIds?.has(t.reply.id)}
              isFading={t.reply && fadingIds?.has(t.reply.id)}
              onToggleSelect={() => t.reply && onToggleSelect?.(t.reply.id)}
              onMarkUsed={() => t.reply && onMarkUsed?.(t.reply.id)}
              onSkip={() => t.reply && onSkip?.(t.reply.id)}
            />
          ))}
        </div>
      ) : (
        <div className="px-5 py-6 text-center text-xs text-zinc-500" data-testid={`no-pending-${post.id}`}>
          All comments handled. Hit Refresh to pull new ones.
        </div>
      )}
    </article>
  );
}

function CommentThread({ thread, workspaceId, onChange, isSelected, isFading, onToggleSelect, onMarkUsed, onSkip }) {
  const { comment, reply } = thread;
  const [regen, setRegen] = useState(false);
  const [editing, setEditing] = useState(null); // variant_id being edited, or null
  const [editedTexts, setEditedTexts] = useState({}); // variant_id → text
  const variants = reply?.variants || [];

  const status = reply?.status || "pending";
  const isUsed = status === "used";
  const isSkipped = status === "skipped";

  const copyVariant = async (variant) => {
    const text = editedTexts[variant.variant_id] ?? variant.comment_text;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Reply copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const startEdit = (v) => {
    setEditing(v.variant_id);
    setEditedTexts((prev) =>
      prev[v.variant_id] !== undefined ? prev : { ...prev, [v.variant_id]: v.comment_text }
    );
  };

  const cancelEdit = (v) => {
    setEditing(null);
    setEditedTexts((prev) => ({ ...prev, [v.variant_id]: v.comment_text }));
  };

  const markUsed = () => {
    if (!reply) return;
    if (onMarkUsed) onMarkUsed();
  };

  const skip = () => {
    if (!reply) return;
    if (onSkip) onSkip();
  };

  const regenerate = async () => {
    if (!reply) return;
    setRegen(true);
    try {
      await apiPost(`/workspaces/${workspaceId}/reply-drafts/${reply.id}/regenerate`, {});
      toast.success("Fresh reply variants generated");
      setEditedTexts({});
      setEditing(null);
      onChange();
    } catch {
      toast.error("Regenerate failed");
    } finally {
      setRegen(false);
    }
  };

  const initials = (comment.commenter_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div
      data-testid={`comment-thread-${comment.id}`}
      className={`bg-graphite/40 border border-hairline p-4 transition-all duration-300 ${
        isUsed || isSkipped ? "opacity-60" : ""
      } ${isFading ? "respond-fade-out" : ""} ${isSelected ? "respond-selected" : ""}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {/* selection checkbox — visible if reply is pending */}
        {reply && reply.status !== "used" && reply.status !== "skipped" && (
          <button
            onClick={onToggleSelect}
            data-testid={`comment-select-${comment.id}`}
            className="mt-1 shrink-0 text-stone-dim hover:text-terracotta transition-colors"
            title="select (E to mark done)"
          >
            {isSelected ? (
              <CheckSquare className="w-4 h-4" style={{ color: "hsl(var(--accent))" }} />
            ) : (
              <Square className="w-4 h-4" />
            )}
          </button>
        )}
        {comment.commenter_avatar ? (
          <img src={comment.commenter_avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-7 h-7 bg-graphite-2 flex items-center justify-center text-xs text-stone shrink-0">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm text-zinc-100 flex items-center gap-2 flex-wrap">
            {comment.commenter_url ? (
              <a href={comment.commenter_url} target="_blank" rel="noreferrer" className="hover:text-sky-300" data-testid={`open-commenter-${comment.id}`}>
                {comment.commenter_name || "Anonymous"}
              </a>
            ) : (
              <span>{comment.commenter_name || "Anonymous"}</span>
            )}
            <span className="overline text-zinc-500" style={{ fontSize: 9 }}>
              {isUsed ? "replied" : isSkipped ? "skipped" : "pending"}
            </span>
          </div>
          {comment.commenter_headline && (
            <div className="text-xs text-zinc-500 truncate">{comment.commenter_headline}</div>
          )}
          <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{comment.comment_text}</p>
        </div>
      </div>

      {reply && variants.length > 0 && (
        <div className="ml-10 mt-3 space-y-2">
          <div className="overline mb-1 flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-sky-400" /> ai replies · pick one
          </div>
          {variants.map((v, idx) => {
            const isEditing = editing === v.variant_id;
            const currentText = editedTexts[v.variant_id] ?? v.comment_text;
            return (
              <div
                key={v.variant_id}
                data-testid={`reply-variant-row-${v.variant_id}`}
                className="bg-zinc-950/60 border border-zinc-800 rounded-md px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="overline text-zinc-500" style={{ fontSize: 9 }}>
                    {idx + 1} · {(v.style || "draft").toLowerCase()}
                  </span>
                  <div className="flex items-center gap-1">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => setEditing(null)}
                          data-testid={`save-variant-${v.variant_id}`}
                          className="text-xs text-emerald-400 hover:text-emerald-200 px-2 py-1"
                          title="Done editing"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => cancelEdit(v)}
                          data-testid={`cancel-variant-${v.variant_id}`}
                          className="text-xs text-zinc-500 hover:text-zinc-300 px-2 py-1"
                          title="Discard edits"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(v)}
                          data-testid={`edit-variant-${v.variant_id}`}
                          className="text-xs text-zinc-500 hover:text-zinc-200 px-2 py-1"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => copyVariant({ ...v, comment_text: currentText })}
                          data-testid={`copy-variant-${v.variant_id}`}
                          className="text-xs text-zinc-300 hover:text-sky-300 px-2 py-1"
                          title="Copy"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <Textarea
                    data-testid={`edit-text-${v.variant_id}`}
                    value={currentText}
                    onChange={(e) =>
                      setEditedTexts((prev) => ({ ...prev, [v.variant_id]: e.target.value }))
                    }
                    rows={3}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100"
                  />
                ) : (
                  <p
                    data-testid={`variant-text-${v.variant_id}`}
                    className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap"
                  >
                    {currentText}
                  </p>
                )}
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={regenerate}
              disabled={regen}
              className="btn-ghost text-xs"
              data-testid={`regen-reply-${reply.id}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 inline ${regen ? "animate-spin" : ""}`} />
              Regenerate all
            </button>
            {!isUsed && (
              <button onClick={markUsed} className="btn-ghost text-xs text-sky-300" data-testid={`used-reply-${reply.id}`}>
                <Check className="w-3.5 h-3.5 mr-1 inline" /> Mark replied
              </button>
            )}
            {!isSkipped && !isUsed && (
              <button onClick={skip} className="btn-ghost text-xs text-zinc-500" data-testid={`skip-reply-${reply.id}`}>
                <X className="w-3.5 h-3.5 mr-1 inline" /> Skip
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FindPostDialog({ workspaceId, onClose, onFound }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/my-posts/find-by-url`, { post_url: url.trim() });
      const runId = r.run_id;
      toast.success("fetching post in the background…", {
        description: "usually 30-60s. closing the dialog is fine.",
      });
      onFound?.();
      // Poll silently in the background and surface the final state.
      const startedAt = Date.now();
      const maxMs = 4 * 60 * 1000;
      while (Date.now() - startedAt < maxMs) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const s = await apiGet(
            `/workspaces/${workspaceId}/my-posts/find-by-url/status${runId ? `?run_id=${runId}` : ""}`
          );
          if (s?.status === "succeeded") {
            const res = s.result || {};
            const linked = res.matched_voice_profile_name || "your voice";
            const newComments = res.new_comments ?? res.comments_added ?? 0;
            const newReplies = res.reply_drafts_created ?? res.replies_created ?? 0;
            toast.success(`post found · matched to ${linked} · ${newComments} comments · ${newReplies} drafts`);
            onFound?.();
            break;
          }
          if (s?.status === "failed") {
            toast.error(s.error || "could not find post", { duration: 12000 });
            break;
          }
        } catch {
          continue;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not find post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-zinc-100">Find a specific post</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <Label className="text-xs text-zinc-400">paste your LinkedIn post URL</Label>
        <Input
          data-testid="find-post-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.linkedin.com/posts/yourname_… "
          className="bg-zinc-900 border-zinc-800 text-zinc-100"
        />
        <p className="text-xs text-zinc-500">
          We'll match it to one of your voice profiles by the URL slug and pull all comments + auto-generate replies.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost" data-testid="find-cancel">Cancel</button>
          <Button onClick={submit} disabled={busy || !url.trim()} className="btn-primary" data-testid="find-submit">
            {busy ? "Fetching…" : "Find post"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}


function ManualCommentDialog({ workspaceId, postId, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !text.trim()) {
      toast.error("Name and comment are required");
      return;
    }
    setBusy(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/my-posts/${postId}/comments`, {
        commenter_name: name.trim(),
        commenter_headline: headline.trim(),
        commenter_url: url.trim(),
        comment_text: text.trim(),
      });
      toast.success(`Comment added · ${r.variants} reply drafts generated`);
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-zinc-100">Add a comment manually</DialogTitle>
      </DialogHeader>
      <p className="text-xs text-zinc-500 -mt-2 mb-2">
        Apify only sees the top ~6 comments per post. Paste any others here and AI will draft replies.
      </p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-zinc-400">commenter name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Saurabh Mishra"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="manual-comment-name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">role / headline (optional)</Label>
            <Input
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="CEO at XYZ"
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="manual-comment-headline"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">linkedin URL (optional)</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://linkedin.com/in/..."
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              data-testid="manual-comment-url"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">comment text *</Label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="paste the comment exactly as they wrote it"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
            data-testid="manual-comment-text"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost" data-testid="manual-comment-cancel">Cancel</button>
          <Button onClick={submit} disabled={busy} className="btn-primary" data-testid="manual-comment-submit">
            {busy ? "Generating…" : "Add + draft replies"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}



function BulkCommentDialog({ workspaceId, postId, onClose, onAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  // Parser: blocks separated by --- (or 3+ dashes).
  // Each block: first non-empty line = "Name | Headline | URL" (headline and URL optional).
  // Remaining lines = comment text.
  const parse = (raw) => {
    const blocks = raw.split(/\n\s*-{3,}\s*\n/).map((b) => b.trim()).filter(Boolean);
    const out = [];
    for (const b of blocks) {
      const lines = b.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) continue;
      const meta = lines[0].split("|").map((s) => s.trim());
      const name = meta[0] || "";
      const headline = meta[1] || "";
      const url = meta[2] || "";
      const body = lines.slice(1).join("\n");
      if (!name || !body) continue;
      out.push({ commenter_name: name, commenter_headline: headline, commenter_url: url, comment_text: body });
    }
    return out;
  };

  const parsed = parse(text);

  const submit = async () => {
    if (parsed.length === 0) {
      toast.error("Nothing parsed — check the format.");
      return;
    }
    setBusy(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/my-posts/${postId}/comments/bulk`, {
        comments: parsed,
      });
      toast.success(
        `Added ${r.added}${r.skipped_duplicates ? ` · skipped ${r.skipped_duplicates} duplicate(s)` : ""} · ${r.drafts_created} replies drafted`
      );
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-zinc-100">Bulk add comments</DialogTitle>
        <DialogDescription className="text-xs text-zinc-500">
          Paste comments separated by --- and AI drafts replies for all of them in parallel.
        </DialogDescription>
      </DialogHeader>
      <p className="text-xs text-zinc-500 -mt-2 mb-2">
        Paste comments separated by <code className="text-zinc-300">---</code>. First line of each block:
        <code className="text-zinc-300 mx-1">name | headline | url</code>
        (only name is required). Remaining lines = the comment.
      </p>
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-3 mb-3">
        <div className="overline mb-1" style={{ fontSize: 9 }}>example</div>
        <pre className="text-xs text-zinc-400 mono whitespace-pre-wrap leading-relaxed">{`Saurabh Mishra | CEO at XYZ | https://linkedin.com/in/saurabh
this is what saurabh said
---
Praneet | Founder
this is praneet's comment
spanning multiple lines`}</pre>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        placeholder="paste comments here…"
        className="bg-zinc-900 border-zinc-800 text-zinc-100 mono text-xs"
        data-testid="bulk-comments-textarea"
      />
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
        <div className="text-xs text-zinc-500" data-testid="bulk-parsed-count">
          {parsed.length === 0
            ? "no comments parsed yet"
            : `${parsed.length} comment${parsed.length === 1 ? "" : "s"} ready`}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="bulk-cancel">Cancel</button>
          <Button
            onClick={submit}
            disabled={busy || parsed.length === 0}
            className="btn-primary"
            data-testid="bulk-submit"
          >
            {busy ? "Generating…" : `Add ${parsed.length || ""} + draft replies`}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}



// ============================================================
// INSPIRE PANEL — paste a creator's caption, get N per-voice comments back.
// ============================================================
function InspirePanel({ workspaceId }) {
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    const text = (caption || "").trim();
    if (text.length < 8) {
      toast.error("paste a caption first (8+ chars)");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const start = await apiPost(
        `/workspaces/${workspaceId}/engagement/inspire-comments`,
        { caption: text, per_voice: 3 },
      );
      const runId = start.run_id;
      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        const s = await apiGet(`/workspaces/${workspaceId}/engagement/inspire-runs/${runId}`);
        if (s.status === "done") {
          setResult(s.result);
          toast.success(`${(s.result?.variants || []).length} comments ready`);
          setBusy(false);
          return;
        }
        if (s.status === "error") throw new Error(s.error || "generation failed");
      }
      throw new Error("generation timed out — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "generation failed");
      setBusy(false);
    }
  };

  // Group variants by voice for the two-column layout.
  const byVoice = (result?.voices || []).map((v) => ({
    ...v,
    variants: (result?.variants || []).filter(
      (x) => (x.voice_profile_id || null) === (v.voice_profile_id || null),
    ),
  }));

  return (
    <div className="space-y-6" data-testid="inspire-panel">
      <div className="surface p-4 space-y-3">
        <div>
          <Label className="text-xs text-zinc-400 flex items-center gap-2">
            <Wand2 className="w-3.5 h-3.5 text-terracotta" />
            paste any creator's caption · we'll generate 3 comments per voice (rotating themes)
          </Label>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={6}
            placeholder="paste a caption from a LinkedIn post you want to engage with. newlines and bold glyphs are preserved verbatim."
            className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"
            data-testid="inspire-caption-input"
            style={{ whiteSpace: "pre-wrap" }}
          />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] text-zinc-500 mono">
            angles rotate every call so themes don't pattern-match.
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { setCaption(""); setResult(null); }}
              disabled={busy || (!caption && !result)}
              className="btn-ghost"
              data-testid="inspire-clear-btn"
            >
              clear
            </Button>
            <Button
              onClick={generate}
              disabled={busy}
              className="btn-primary"
              data-testid="inspire-generate-btn"
            >
              {busy
                ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
              {busy ? "thinking…" : (result ? "regenerate" : "generate 6 comments")}
            </Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {byVoice.map((v) => (
            <InspireVoiceColumn key={v.voice_profile_id || "none"} voice={v} />
          ))}
        </div>
      )}

      {!result && !busy && (
        <div className="surface p-8 text-center text-zinc-500 text-sm">
          paste a caption above and click generate.
          <div className="mono text-[10px] text-zinc-600 mt-2">
            tip: works best with the full post text, not just the hook.
          </div>
        </div>
      )}
    </div>
  );
}

function InspireVoiceColumn({ voice }) {
  const vc = voiceColor(voice.voice_profile_id);
  return (
    <div
      className="surface p-4"
      style={{ borderLeft: `3px solid ${vc.swatch}` }}
      data-testid={`inspire-voice-col-${voice.voice_profile_id || "none"}`}
    >
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-zinc-800">
        <VoiceChip
          voiceId={voice.voice_profile_id}
          voiceName={voice.voice_profile_name}
          variant="dark"
          size="md"
          testid={`inspire-voice-chip-${voice.voice_profile_id || "none"}`}
        />
        <div className="mono text-[10px] uppercase tracking-wider text-zinc-500">
          {(voice.angles || []).join(" · ")}
        </div>
      </div>
      <div className="space-y-3">
        {voice.variants.map((v) => (
          <InspireVariantCard key={v.variant_id} v={v} />
        ))}
      </div>
    </div>
  );
}

function InspireVariantCard({ v }) {
  return (
    <div
      className="bg-zinc-950/70 border border-zinc-800 p-3"
      data-testid={`inspire-variant-${v.variant_id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="mono text-[10px] uppercase tracking-wider text-zinc-500">
          {v.style}
        </span>
        <CopyIcon
          text={v.comment_text}
          testid={`inspire-copy-${v.variant_id}`}
          label="copy"
          className="text-zinc-500 hover:text-zinc-200"
        />
      </div>
      <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
        {v.comment_text}
      </p>
    </div>
  );
}

// ============================================================
// REPLY LAB — paste your post + their comments, get reply drafts.
// ============================================================
function ReplyLabPanel({ workspaceId, voiceProfiles }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try {
      const list = await apiGet(`/workspaces/${workspaceId}/manual-posts`);
      setPosts(list || []);
      if (!activeId && list?.length > 0) setActiveId(list[0].id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId]);

  if (loading) return <div className="text-zinc-500 text-sm">loading reply lab…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" data-testid="reply-lab-panel">
      <aside className="space-y-3">
        <ReplyLabNewPostForm
          workspaceId={workspaceId}
          voiceProfiles={voiceProfiles}
          onCreated={async (p) => { await load(); setActiveId(p.id); }}
        />
        <div className="space-y-1.5">
          <div className="overline mb-1">your saved posts</div>
          {posts.length === 0 && (
            <div className="text-xs text-zinc-500 italic px-2 py-3">
              no posts yet. paste one on the right to start.
            </div>
          )}
          {posts.map((p) => (
            <ReplyLabPostListRow
              key={p.id}
              post={p}
              active={p.id === activeId}
              onSelect={() => setActiveId(p.id)}
              onDelete={async () => {
                if (!window.confirm("delete this post and all its replies?")) return;
                try {
                  await apiDelete(`/workspaces/${workspaceId}/manual-posts/${p.id}`);
                  toast.success("deleted");
                  await load();
                  if (activeId === p.id) setActiveId(null);
                } catch {
                  toast.error("delete failed");
                }
              }}
            />
          ))}
        </div>
      </aside>

      <div>
        {activeId ? (
          <ReplyLabPostDetail
            workspaceId={workspaceId}
            postId={activeId}
            key={activeId}
            onChanged={load}
          />
        ) : (
          <div className="surface p-8 text-center text-zinc-500 text-sm">
            create or pick a post to start drafting replies.
          </div>
        )}
      </div>
    </div>
  );
}

function ReplyLabNewPostForm({ workspaceId, voiceProfiles, onCreated }) {
  const [caption, setCaption] = useState("");
  const [title, setTitle] = useState("");
  const [vpId, setVpId] = useState(
    () => voiceProfiles?.find((v) => v.is_default)?.id || voiceProfiles?.[0]?.id || null
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const cap = caption || "";
    if (cap.trim().length < 8) {
      toast.error("paste your post caption first");
      return;
    }
    if (!vpId) {
      toast.error("pick a voice profile");
      return;
    }
    setBusy(true);
    try {
      const p = await apiPost(`/workspaces/${workspaceId}/manual-posts`, {
        caption: cap, voice_profile_id: vpId, title: title.trim() || null,
      });
      toast.success(`saved · ${p.voice_profile_name}`);
      setCaption("");
      setTitle("");
      onCreated?.(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface p-3 space-y-2" data-testid="reply-lab-new-post-form">
      <div className="overline mb-1">paste a new post</div>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="title (optional)"
        className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs"
        data-testid="reply-lab-title-input"
      />
      <Textarea
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        rows={5}
        placeholder="paste the post caption here (verbatim — newlines + bold glyphs preserved)"
        className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs"
        data-testid="reply-lab-caption-input"
        style={{ whiteSpace: "pre-wrap" }}
      />
      <div className="flex items-center gap-2">
        <select
          value={vpId || ""}
          onChange={(e) => setVpId(e.target.value || null)}
          className="bg-zinc-900 border border-zinc-800 text-zinc-100 text-xs px-2 py-1.5 flex-1"
          data-testid="reply-lab-voice-select"
        >
          {(voiceProfiles || []).map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}{v.is_default ? " (default)" : ""}
            </option>
          ))}
        </select>
        <Button
          onClick={submit}
          disabled={busy}
          className="btn-primary"
          data-testid="reply-lab-save-post-btn"
        >
          {busy ? "saving…" : "save post"}
        </Button>
      </div>
    </div>
  );
}

function ReplyLabPostListRow({ post, active, onSelect, onDelete }) {
  const vc = voiceColor(post.voice_profile_id);
  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer px-3 py-2 border ${active ? "border-terracotta bg-zinc-900" : "border-zinc-800 hover:border-zinc-700"}`}
      style={{ borderLeft: `3px solid ${vc.swatch}` }}
      data-testid={`reply-lab-post-row-${post.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-100 truncate">
            {post.title || (post.caption || "").trim().slice(0, 40) + "…"}
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <VoiceChip voiceId={post.voice_profile_id} voiceName={post.voice_profile_name} variant="dark" size="sm" />
            <span className="mono text-[10px] text-zinc-500">
              {post.comment_count || 0} {(post.comment_count || 0) === 1 ? "comment" : "comments"}
            </span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-zinc-600 hover:text-rose-400 transition-colors p-1"
          title="delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function ReplyLabPostDetail({ workspaceId, postId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await apiGet(`/workspaces/${workspaceId}/manual-posts/${postId}`);
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  // Poll while any reply batch is still generating in the background.
  const anyGenerating = (data?.comments || []).some((c) => c.gen_status === "generating");
  useEffect(() => {
    if (!anyGenerating) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [anyGenerating]);

  if (loading || !data) return <div className="text-zinc-500 text-sm">loading…</div>;
  const { post, comments } = data;

  const submitBulk = async () => {
    const text = bulkText || "";
    // Split on blank lines. Each chunk = one comment. First line, if formatted
    // "Name: comment", is treated as author + text.
    const chunks = text.split(/\n\s*\n/).map((c) => c.trim()).filter(Boolean);
    if (chunks.length === 0) {
      toast.error("paste comments first");
      return;
    }
    const items = chunks.map((c) => {
      const m = c.match(/^([^:\n]{1,80}):\s*([\s\S]+)$/);
      if (m) return { author_name: m[1].trim(), text: m[2] };
      return { author_name: "", text: c };
    });
    setBusy(true);
    try {
      const r = await apiPost(
        `/workspaces/${workspaceId}/manual-posts/${postId}/comments`,
        { comments: items },
      );
      toast.success(`Drafting replies for ${r.created.length} comment${r.created.length === 1 ? "" : "s"}…`);
      setBulkText("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "generation failed");
    } finally {
      setBusy(false);
    }
  };

  const vc = voiceColor(post.voice_profile_id);

  return (
    <div className="space-y-5">
      {/* Saved post */}
      <div className="surface p-4" style={{ borderLeft: `3px solid ${vc.swatch}` }} data-testid="reply-lab-post-detail">
        <div className="flex items-start justify-between mb-3 pb-3 border-b border-zinc-800">
          <div className="flex items-center gap-3 flex-wrap">
            {post.title && <h2 className="serif text-xl text-zinc-100">{post.title}</h2>}
            <VoiceChip voiceId={post.voice_profile_id} voiceName={post.voice_profile_name} variant="dark" size="md" />
          </div>
          <CopyIcon
            text={post.caption}
            testid={`reply-lab-post-copy-${post.id}`}
            label="copy"
            className="text-zinc-500 hover:text-zinc-200"
          />
        </div>
        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-zinc-300 leading-relaxed m-0">
{post.caption}
        </pre>
      </div>

      {/* Add comments */}
      <div className="surface p-4 space-y-2" data-testid="reply-lab-bulk-comments">
        <Label className="text-xs text-zinc-400">paste comments (single or bulk · blank line between each · optional <span className="mono">Name: comment</span> format)</Label>
        <Textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={6}
          placeholder={"Rohit Sharma: this is so true. our floor managers stopped doing daily walks.\n\nPriya Mehta: i think workshops are still important though."}
          className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs"
          style={{ whiteSpace: "pre-wrap" }}
          data-testid="reply-lab-bulk-input"
        />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] text-zinc-500 mono">
            we&apos;ll generate 3 reply drafts per comment in {post.voice_profile_name}&apos;s voice.
          </div>
          <Button
            onClick={submitBulk}
            disabled={busy}
            className="btn-primary"
            data-testid="reply-lab-generate-btn"
          >
            {busy
              ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            {busy ? "drafting…" : "generate replies"}
          </Button>
        </div>
      </div>

      {/* Comment list */}
      <div className="space-y-3">
        {comments.length === 0 ? (
          <div className="surface p-6 text-center text-zinc-500 text-sm">no comments yet.</div>
        ) : (
          comments.map((c) => (
            <ReplyLabCommentCard
              key={c.id}
              workspaceId={workspaceId}
              comment={c}
              onChanged={async () => { await load(); onChanged?.(); }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ReplyLabCommentCard({ workspaceId, comment, onChanged }) {
  const [busy, setBusy] = useState(false);

  const regenerate = async () => {
    setBusy(true);
    try {
      await apiPost(`/workspaces/${workspaceId}/manual-comments/${comment.id}/regenerate`, {});
      toast.success("Drafting new replies…");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("delete this comment + its drafts?")) return;
    try {
      await apiDelete(`/workspaces/${workspaceId}/manual-comments/${comment.id}`);
      await onChanged?.();
    } catch {
      toast.error("delete failed");
    }
  };

  return (
    <div className="surface p-4" data-testid={`reply-lab-comment-${comment.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-100 font-medium">
            {comment.author_name || "anonymous"}
          </div>
          <pre className="whitespace-pre-wrap break-words font-sans text-xs text-zinc-300 mt-1 leading-relaxed m-0">
{comment.text}
          </pre>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={regenerate}
            disabled={busy}
            className="text-zinc-500 hover:text-zinc-200 p-1.5 transition-colors"
            title="regenerate"
            data-testid={`reply-lab-regen-${comment.id}`}
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={del}
            className="text-zinc-600 hover:text-rose-400 p-1.5 transition-colors"
            title="delete"
            data-testid={`reply-lab-delete-${comment.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      {comment.gen_status === "generating" ? (
        <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 text-xs text-zinc-500" data-testid={`reply-lab-generating-${comment.id}`}>
          <RefreshCw className="w-3 h-3 animate-spin text-terracotta" />
          drafting 3 replies…
        </div>
      ) : comment.gen_status === "error" ? (
        <div className="mt-3 pt-3 border-t border-zinc-800 text-xs text-red-400" data-testid={`reply-lab-error-${comment.id}`}>
          {comment.gen_error || "generation failed"} — hit regenerate to retry.
        </div>
      ) : (
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 pt-3 border-t border-zinc-800">
        {(comment.variants || []).map((v) => (
          <div
            key={v.variant_id}
            className="bg-zinc-950/70 border border-zinc-800 p-2.5"
            data-testid={`reply-lab-variant-${v.variant_id}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="mono text-[10px] uppercase tracking-wider text-zinc-500">
                {v.style}
              </span>
              <CopyIcon
                text={v.comment_text}
                testid={`reply-lab-copy-${v.variant_id}`}
                label=""
                className="text-zinc-500 hover:text-zinc-200"
              />
            </div>
            <p className="text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed">
              {v.comment_text}
            </p>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

